import produce, { enableES5 } from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import { CreateCandidateFormLocationStateFormData } from './useCreateCandidateForm';
import { IExamContentResponse, IExamMasterCodeItem } from '../../../api/user/model';
import {
  MyInfoDataResponseItem,
  PostMyInfoTokenResponse,
} from '../../../api-refactored/user/usePostMyInfoToken';
import {
  getTargetCitizenshipCodeItem,
  transformPrOptions,
} from '../../accountManagement/logic/usePrCitizenshipForm';
import {preferredContactType} from "../../../models";

enableES5();

const MYINFO_RESIDENTIAL_STATUS_TO_MASTER_CODE_MAPPING: {
  myInfoCode: string;
  masterCode: string;
}[] = [
  { myInfoCode: 'A', masterCode: 'A' }, // ALIEN
  { myInfoCode: 'C', masterCode: 'S' }, // SINGAPORE CITIZEN
  { myInfoCode: 'P', masterCode: 'P' }, // SINGAPORE PR
];

const noMatchingMyInfoValueWithIExamMasterCodeItem = (
  valueFromIExamMasterQuery?: IExamMasterCodeItem,
  myInfoValue = ''
) => valueFromIExamMasterQuery === undefined && myInfoValue !== '';

/**
 * Return an item from master code API which matches data from MyInfo API. Return undefined if MyInfo API data could
 * not be found in master code API.
 *
 * @param masterCodeList
 * @param myInfoItem
 */
const getMyInfoCodeFromMasterCode = (
  masterCodeList: IExamMasterCodeItem[],
  myInfoItem: MyInfoDataResponseItem
): IExamMasterCodeItem | undefined => {
  if (!myInfoItem.value) {
    return undefined;
  }
  if (myInfoItem.value === 'BRITISH OVERSEAS TERRITORIES CITIZEN') {
    return masterCodeList.find(
      ({ codeValueDescription }) =>
        codeValueDescription.toLowerCase() === 'BRITISH NAT OVERSEAS'.toLowerCase()
    ) as IExamMasterCodeItem;
  }
  return masterCodeList.find(
    ({ codeValueDescription }) =>
      codeValueDescription.toLowerCase() === myInfoItem.value.toLowerCase()
  ) as IExamMasterCodeItem;
};

const getMyInfoCountryCodeFromMasterCode = (
  masterCodeList: IExamMasterCodeItem[],
  myInfoItem: MyInfoDataResponseItem
): IExamMasterCodeItem | undefined =>
  masterCodeList.find(
    ({ codeValue }) => codeValue.toLowerCase() === myInfoItem.value.toLowerCase()
  ) as IExamMasterCodeItem;

const handlingForPrStatus = (
  fieldsWithError: string[],
  myInfoItem: MyInfoDataResponseItem,
  transformedPrOption: IExamMasterCodeItem[]
) => {
  const prStatusMasterCode = MYINFO_RESIDENTIAL_STATUS_TO_MASTER_CODE_MAPPING.find(
    (mapItem) => mapItem.myInfoCode === myInfoItem.code
  )?.masterCode;
  return transformedPrOption.find(
    ({ codeValue }) => codeValue === prStatusMasterCode
  ) as IExamMasterCodeItem;
};

/**
 * This function map data from MyInfo API to an object that could be used to populate CreateCandidateForm.
 *
 * @param myInfoApiRes
 * @param iExamMasterCodeQueryData
 */
export const getCreateCandidateFormData = (
  myInfoApiRes: PostMyInfoTokenResponse,
  iExamMasterCodeQueryData: IExamContentResponse
) => {
  const fieldsWithError: string[] = [];
  const fieldsMandatoryButNull: string[] = [];

  let createCandidateFormData: CreateCandidateFormLocationStateFormData = {
    formDataDefaultValues: {},
    formDataIsDisabled: {},
  };

  const transformedPrOption = transformPrOptions(iExamMasterCodeQueryData.prStatus);

  const checkName = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      // @ts-ignore
      draft.formDataDefaultValues.name = myInfoItem.value;
      // @ts-ignore
      draft.formDataIsDisabled.name = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkEmail = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      // @ts-ignore
      draft.formDataDefaultValues.email = myInfoItem.value;
    }
  };

  const checkUinFin = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      // @ts-ignore
      draft.formDataDefaultValues.NricFin = myInfoItem.value;
      draft.formDataIsDisabled.NricFin = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkRace = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    const raceCode = getMyInfoCodeFromMasterCode(iExamMasterCodeQueryData.race, myInfoItem);
    if (raceCode) {
      draft.formDataDefaultValues.race = raceCode;
      draft.formDataIsDisabled.race = !myInfoItem.editable;
      if (noMatchingMyInfoValueWithIExamMasterCodeItem(raceCode, myInfoItem.value)) {
        fieldsWithError.push(myInfoItem.field);
      }
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkNationality = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    const nationalityCode = getMyInfoCodeFromMasterCode(
      iExamMasterCodeQueryData.nationality,
      myInfoItem
    );
    if (nationalityCode) {
      draft.formDataDefaultValues.nationality = nationalityCode;
      draft.formDataIsDisabled.nationality = !myInfoItem.editable;
      if (noMatchingMyInfoValueWithIExamMasterCodeItem(nationalityCode, myInfoItem.value)) {
        fieldsWithError.push(myInfoItem.field);
      }
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkSex = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    const genderCode = getMyInfoCodeFromMasterCode(iExamMasterCodeQueryData.gender, myInfoItem);
    if (genderCode) {
      draft.formDataDefaultValues.gender = genderCode;
      draft.formDataIsDisabled.gender = !myInfoItem.editable;
      if (noMatchingMyInfoValueWithIExamMasterCodeItem(genderCode, myInfoItem.value)) {
        fieldsWithError.push(myInfoItem.field);
      }
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkDob = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.dateOfBirth = myInfoItem.value;
      draft.formDataIsDisabled.dateOfBirth = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };
  const checkRegisteredAddressBlockNumber = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.houseOrBlkNo = myInfoItem.value;
      draft.formDataIsDisabled.houseOrBlkNo = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  const checkRegisteredAddressUnitNumber = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.unit = myInfoItem.value;
    } else {
      draft.formDataDefaultValues.unit = "";
    }
    draft.formDataIsDisabled.unit = !myInfoItem.editable;
  };
  const checkRegisteredAddressFloorNumber = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.floor = myInfoItem.value;
    } else {
      draft.formDataDefaultValues.floor = "";
    }
    draft.formDataIsDisabled.floor = !myInfoItem.editable;
  };
  const checkRegisteredAddressStreetNumber = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.streetName = myInfoItem.value;
      draft.formDataIsDisabled.streetName = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };
  const checkRegisteredAddressPostalCode = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.postalCode = myInfoItem.value;
      draft.formDataIsDisabled.postalCode = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };
  const checkHanyupinyinname = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.hanyupinyinName = myInfoItem.value;
      draft.formDataIsDisabled.hanyupinyinName = !myInfoItem.editable;
    }
  };
  const checkResidentialstatus = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    draft.formDataDefaultValues.prStatus = handlingForPrStatus(
      fieldsWithError,
      myInfoItem,
      transformedPrOption
    );
    draft.formDataIsDisabled.prStatus = !myInfoItem.editable;
  };
  const checkMobilenoAreaCode = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.mobileCountryCode = getMyInfoCountryCodeFromMasterCode(
        iExamMasterCodeQueryData.countryCode,
        myInfoItem
      );
    }
  };
  const checkMobilenoDigit = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.mobileContactNumber = myInfoItem.value;
    }
  };
  const checkHomenoAreaCode = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.telephoneCountryCode = getMyInfoCountryCodeFromMasterCode(
        iExamMasterCodeQueryData.countryCode,
        myInfoItem
      );
      draft.formDataIsDisabled.telephoneCountryCode = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };
  const checkHomenoDigit = (
    myInfoItem: MyInfoDataResponseItem,
    draft: WritableDraft<CreateCandidateFormLocationStateFormData>
  ) => {
    if (myInfoItem.value) {
      draft.formDataDefaultValues.telephoneContactNumber = myInfoItem.value;
      draft.formDataIsDisabled.telephoneContactNumber = !myInfoItem.editable;
    } else {
      fieldsMandatoryButNull.push(myInfoItem.field);
    }
  };

  createCandidateFormData = produce(createCandidateFormData, (draft) => {
    // IdType must be I (NRIC or FIN) because it's pre-populated from MyInfo
    draft.formDataDefaultValues.IdType = 'I';
    draft.formDataDefaultValues.sameAsRegisteredAddress = 'R';
    draft.formDataIsDisabled.IdType = true;
    // mobile number and email always editable for account creation
    draft.formDataIsDisabled.email = false;
    draft.formDataIsDisabled.mobileContactNumber = false;
    draft.formDataIsDisabled.mobileCountryCode = false;
    myInfoApiRes.forEach((myInfoItem) => {
      switch (myInfoItem.field) {
        case 'name':
          checkName(myInfoItem, draft);
          break;
        case 'email':
          checkEmail(myInfoItem, draft);
          break;
        case 'uinfin':
          checkUinFin(myInfoItem, draft);
          break;
        case 'race':
          checkRace(myInfoItem, draft);
          break;
        case 'nationality':
          checkNationality(myInfoItem, draft);
          break;
        case 'sex':
          checkSex(myInfoItem, draft);
          break;
        case 'dob':
          checkDob(myInfoItem, draft);
          break;
        case 'registeredAddressBlockNumber':
          checkRegisteredAddressBlockNumber(myInfoItem, draft);
          break;
        case 'registeredAddressUnitNumber':
          checkRegisteredAddressUnitNumber(myInfoItem, draft);
          break;
        case 'registeredAddressFloorNumber':
          checkRegisteredAddressFloorNumber(myInfoItem, draft);
          break;
        case 'registeredAddressStreetNumber':
          checkRegisteredAddressStreetNumber(myInfoItem, draft);
          break;
        case 'registeredAddressPostalCode':
          checkRegisteredAddressPostalCode(myInfoItem, draft);
          break;
        case 'hanyupinyinname':
          checkHanyupinyinname(myInfoItem, draft);
          break;
        case 'secondaryrace':
          draft.formDataDefaultValues.secondaryRace = myInfoItem.value;
          draft.formDataIsDisabled.secondaryRace = !myInfoItem.editable;
          break;
        case 'residentialstatus':
          checkResidentialstatus(myInfoItem, draft);
          break;
        case 'mobilenoAreaCode':
          checkMobilenoAreaCode(myInfoItem, draft);
          break;
        case 'mobilenoDigit':
          checkMobilenoDigit(myInfoItem, draft);
          break;
        case 'regadd':
          fieldsMandatoryButNull.push(myInfoItem.field);
          break;
        case 'homenoAreaCode':
          checkHomenoAreaCode(myInfoItem, draft);
          break;
        case 'homenoDigit':
          checkHomenoDigit(myInfoItem, draft);
          break;
        default:
          break;
      }
    });
    draft.formDataIsDisabled.citizenship = true;
    draft.formDataDefaultValues.preferredAddress = 'L';
    draft.formDataDefaultValues.preferredTelephoneOrMobile = 'M';

    if (draft.formDataDefaultValues.prStatus) {
      const prStatusDesc = draft.formDataDefaultValues.prStatus.codeValueDescription;
      draft.formDataDefaultValues.citizenship = getTargetCitizenshipCodeItem(
        prStatusDesc,
        iExamMasterCodeQueryData.citizenship
      ) as IExamMasterCodeItem;
    }
  });

  return { createCandidateFormData, fieldsWithError, fieldsMandatoryButNull };
};
